import { useCallback, useRef, useState } from 'react';
import RichTextEditor from '../common/RichTextEditor';
import TagInput from '../common/TagInput';
import SeoPreview from './SeoPreview';
import { BLOG_CATEGORIES, OTHERS_CATEGORY } from '../../constants/blogCategories';
import { SITE_AUTHOR } from '../../constants/site';
import { ACCEPT_ATTR, uploadImage, uploadImages } from '../../utils/uploadImage';
import { sanitizeHtml, htmlToText } from '../../utils/sanitizeHtml';
import { imageFallback } from '../../utils/env';

export const EMPTY_POST = {
  blogId: null,
  title: '',
  excerpt: '',
  category: '',
  tags: [],
  author: SITE_AUTHOR,
  image: '',
  content: '',
  urlLink: '',
  featured: false,
  createdOn: '',
};

const EXCERPT_MAX = 155;

/**
 * Create / edit form for a post.
 *
 * Field set matches the BlogDetail contract in src/api/blogs.js exactly, so the
 * payload can go straight to the Spring Boot service with no mapping layer.
 *
 * @param {object}   initial   post to edit, or EMPTY_POST for a new one
 * @param {boolean}  isEditing
 * @param {function} onSave    async (post) => void; throws to report an error
 * @param {function} onCancel
 */
function BlogForm({ initial = EMPTY_POST, isEditing = false, onSave, onCancel }) {
  const [post, setPost] = useState(initial);
  const [coverBusy, setCoverBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const coverInputRef = useRef(null);

  const set = (field) => (e) => {
    const el = e.target;
    setPost((p) => ({ ...p, [field]: el.type === 'checkbox' ? el.checked : el.value }));
  };

  const setContent = useCallback((html) => setPost((p) => ({ ...p, content: html })), []);

  // The editor's image button hands us the files and expects URLs back.
  const handleEditorImages = useCallback((files) => uploadImages(files, 'blogs/body'), []);

  const handleCover = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    setCoverBusy(true);
    try {
      const url = await uploadImage(file, 'blogs/covers');
      setPost((p) => ({ ...p, image: url }));
    } catch (err) {
      setError(err.message);
    } finally {
      setCoverBusy(false);
    }
  };

  /** Fill the excerpt from the opening of the body — better than leaving it blank. */
  const suggestExcerpt = () => {
    const text = htmlToText(post.content);
    if (!text) {
      setError('Write some content first.');
      return;
    }
    const cut = text.slice(0, EXCERPT_MAX);
    const lastStop = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('? '), cut.lastIndexOf('! '));
    setPost((p) => ({
      ...p,
      excerpt: lastStop > 60 ? cut.slice(0, lastStop + 1) : cut.trimEnd(),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Sanitise here, not on render: what gets stored is what was checked.
    const content = sanitizeHtml(post.content);

    if (!post.title.trim()) return setError('Title is required.');
    if (!post.category) return setError('Category is required.');
    if (!htmlToText(content)) return setError('Content is required.');

    setSaving(true);
    try {
      await onSave({
        ...post,
        title: post.title.trim(),
        excerpt: post.excerpt.trim(),
        author: post.author.trim() || SITE_AUTHOR,
        urlLink: post.urlLink.trim(),
        content,
        // Datetime, not a date string: the article page and JSON-LD both format
        // it, and "2026-08-05" sorts and renders inconsistently against ISO values
        // already in the store.
        createdOn: post.createdOn
          ? new Date(post.createdOn).toISOString()
          : new Date().toISOString(),
      });
    } catch (err) {
      setError(err.message);
      setSaving(false);
      return;
    }
    setSaving(false);
  };

  const busy = saving || coverBusy;
  const excerptOver = post.excerpt.length > EXCERPT_MAX;

  // <input type="date"> needs yyyy-MM-dd; the store holds a full ISO string.
  const dateValue = post.createdOn ? String(post.createdOn).slice(0, 10) : '';

  return (
    <form className="admin-card" onSubmit={handleSubmit} noValidate>
      <div className="admin-card-head">
        <h2 className="admin-card-title">{isEditing ? 'Edit post' : 'New post'}</h2>
        {isEditing && (
          <span className="chip chip-sm">
            id {post.blogId}
          </span>
        )}
      </div>

      {error && (
        <div className="admin-msg is-error" role="alert">
          {error}
        </div>
      )}

      <div className="admin-form-grid">
        <label className="field field-full">
          <span>
            Title <em>required</em>
          </span>
          <input
            name="title"
            value={post.title}
            onChange={set('title')}
            placeholder="e.g. Designing a Blog Backend with Spring Boot Microservices"
            autoComplete="off"
          />
        </label>

        <label className="field field-full">
          <span>
            Excerpt <em>used as the meta description and card summary</em>
          </span>
          <textarea
            name="excerpt"
            value={post.excerpt}
            onChange={set('excerpt')}
            rows={3}
            placeholder="One or two sentences summarising the article."
          />
          <span className={`field-counter ${excerptOver ? 'is-over' : ''}`}>
            {post.excerpt.length}/{EXCERPT_MAX}
            <button type="button" className="link-btn" onClick={suggestExcerpt}>
              Suggest from content
            </button>
          </span>
        </label>

        <label className="field">
          <span>
            Category <em>required</em>
          </span>
          <select name="category" value={post.category} onChange={set('category')}>
            <option value="">Select a category…</option>
            {BLOG_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
            <option value={OTHERS_CATEGORY}>{OTHERS_CATEGORY}</option>
          </select>
        </label>

        <label className="field">
          <span>Author</span>
          <input name="author" value={post.author} onChange={set('author')} autoComplete="off" />
        </label>

        <div className="field field-full">
          <span className="field-label">
            Tags <em>drive the related-articles links</em>
          </span>
          <TagInput
            value={post.tags}
            onChange={(tags) => setPost((p) => ({ ...p, tags }))}
          />
        </div>

        <div className="field field-full">
          <span className="field-label">
            Cover image <em>shown on cards, and as the social share image</em>
          </span>
          <div className="cover-row">
            <input
              type="file"
              accept={ACCEPT_ATTR}
              ref={coverInputRef}
              onChange={handleCover}
              disabled={coverBusy}
            />
            {post.image && (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => setPost((p) => ({ ...p, image: '' }))}
              >
                Remove
              </button>
            )}
          </div>
          <input
            className="cover-url"
            name="image"
            value={post.image}
            onChange={set('image')}
            placeholder="…or paste an image URL, e.g. https://cdn.example.com/cover.webp"
            autoComplete="off"
          />
          {coverBusy && <small>Compressing and uploading…</small>}
          {post.image && !coverBusy && (
            <div className="image-preview">
              <img
                src={post.image}
                alt="Cover preview"
                onError={imageFallback('/default-blog.svg')}
              />
            </div>
          )}
        </div>

        <label className="field">
          <span>Publish date</span>
          <input type="date" name="createdOn" value={dateValue} onChange={set('createdOn')} />
        </label>

        <label className="field">
          <span>
            Source link <em>optional</em>
          </span>
          <input
            name="urlLink"
            value={post.urlLink}
            onChange={set('urlLink')}
            placeholder="https://…"
            autoComplete="off"
          />
        </label>

        <label className="field field-full field-check">
          <input type="checkbox" name="featured" checked={post.featured} onChange={set('featured')} />
          <span>
            Feature on the home page
            <em>Takes the large hero slot. Setting it here unsets it on every other post.</em>
          </span>
        </label>

        <div className="field field-full">
          <span className="field-label">
            Content <em>required</em>
          </span>
          <RichTextEditor
            value={post.content}
            onChange={setContent}
            onInsertImages={handleEditorImages}
          />
        </div>

        <div className="field field-full">
          <SeoPreview post={post} />
        </div>
      </div>

      <div className="admin-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {saving ? 'Saving…' : isEditing ? 'Update post' : 'Publish post'}
        </button>
        <button type="button" className="btn btn-ghost" disabled={busy} onClick={onCancel}>
          {isEditing ? 'Cancel edit' : 'Clear form'}
        </button>
      </div>
    </form>
  );
}

export default BlogForm;
